import { useState, useEffect } from "react";
import { supabase, BRAND, FAINT, pageTitle, text, btn, linkBtn, errorText, savedText, WHITE, LINE, INK, MUTED, ROW_LINE, TEXT, GREEN } from "./constants";
import { useIsMobile } from "./lib/responsive";
import { ConfirmDialog } from "./shared";

// ─── GESTIONAR CUENTA ─────────────────────────────────────────────────────────
// Rehecha el 09/10 con el lenguaje de Home y Nueva posición: tarjetas blancas,
// filas "etiqueta | campo" y la misma rejilla de dos columnas que Home.
//   Izquierda: Empresa (se guarda sola al salir del campo) y Facturación
//     (un botón, porque son seis campos que van juntos).
//   Derecha: Integraciones (Calendly) y, al final, la baja de cuenta.
// Todas las escrituras en `clients` piden .select() y comprueban que hubo fila:
// un update bloqueado por RLS devuelve 200 con 0 filas, no un error.

// 31/10 (simplificación): el cliente ve solo Empresa y la baja. Facturación y
// el token de Calendly quedan para el admin, que los rellena por el cliente:
// la facturación aún no se usa y el token es trabajo técnico que no le toca.
const AccountView = ({ t, lang = "en", companyName = "", userEmail = "", selectedClientId = null, onCompanyNameChange = null, isAdmin = false }) => {
  const es = lang === "es";
  const isMobile = useIsMobile();

  const [calendlyToken, setCalendlyToken] = useState("");
  const [savedToken, setSavedToken] = useState(""); // lo que hay en la base: decide "Conectado"
  const [tokenLoaded, setTokenLoaded] = useState(false);
  const [tokenSaving, setTokenSaving] = useState(false);
  const [tokenStatus, setTokenStatus] = useState(null); // null | 'saved' | 'error'
  const [showToken, setShowToken] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // Baja de cuenta: derecho de supresión del RGPD. No es opcional y no existía.
  const [showDelete, setShowDelete] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");

  // No se borra desde el navegador: eliminar el usuario de auth exige permisos
  // de servicio que nunca deben estar aquí. Se registra la petición —lo que
  // además deja constancia de quién pidió qué y cuándo— y se ejecuta después.
  // (Sin .select(): pedir la fila de vuelta exigiría una regla de lectura sobre
  // deletion_requests, y un insert bloqueado ya devuelve error.)
  const requestDeletion = async () => {
    if (deleteText !== "DELETE") return;
    setDeleting(true); setDeleteErr("");
    const { error } = await supabase.from("deletion_requests").insert({
      email: userEmail || "",
      note: `Cliente: ${companyName || "-"} · client_id: ${selectedClientId || "-"}`,
    });
    setDeleting(false);
    if (error) { setDeleteErr(error.message); return; }
    await supabase.auth.signOut();
  };

  // Nombre de la empresa — se guarda al salir del campo
  const [name, setName] = useState(companyName);
  const [nameSaving, setNameSaving] = useState(false);
  const [nameStatus, setNameStatus] = useState(null); // null | 'saved' | 'error' | 'empty'

  // Nombre de contacto — se guarda al salir del campo
  const [contactName, setContactName] = useState("");
  const [contactOriginal, setContactOriginal] = useState("");
  const [contactLoaded, setContactLoaded] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactStatus, setContactStatus] = useState(null);

  // Datos de facturación (NIF + dirección + forma de pago preferida)
  const [billing, setBilling] = useState({ tax_id: "", address: "", city: "", postal_code: "", country: "", payment_method: "" });
  const [billingLoaded, setBillingLoaded] = useState(false);
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingOriginal, setBillingOriginal] = useState(null); // para saber si hay cambios

  // El nombre sigue a la prop (al cambiar de cliente o tras guardarlo). El aviso
  // solo se limpia al cambiar de cliente: si no, "Guardado" desaparecería al
  // instante, porque guardar actualiza la prop.
  useEffect(() => { setName(companyName); }, [companyName]);
  useEffect(() => { setNameStatus(null); setContactStatus(null); setBillingStatus(null); setTokenStatus(null); }, [selectedClientId]);

  // Contacto + facturación del cliente activo
  useEffect(() => {
    if (!selectedClientId) { setContactLoaded(true); setBillingLoaded(true); return; }
    supabase
      .from("clients")
      .select("contact_name,billing_tax_id,billing_address,billing_city,billing_postal_code,billing_country,billing_payment_method")
      .eq("id", selectedClientId)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setContactName(data.contact_name || "");
          setContactOriginal(data.contact_name || "");
          const billingData = {
            tax_id: data.billing_tax_id || "",
            address: data.billing_address || "",
            city: data.billing_city || "",
            postal_code: data.billing_postal_code || "",
            country: data.billing_country || "",
            payment_method: data.billing_payment_method || "",
          };
          setBilling(billingData);
          setBillingOriginal(billingData);
        }
        setContactLoaded(true);
        setBillingLoaded(true);
      });
  }, [selectedClientId]);

  // Escritura común en `clients`: comprueba que se escribió una fila
  const updateClient = async (payload) => {
    const { data, error } = await supabase.from("clients").update(payload).eq("id", selectedClientId).select("id");
    if (error) console.error("Error saving account:", error);
    return !error && data && data.length > 0;
  };

  const handleSaveName = async () => {
    if (!selectedClientId || nameSaving) return;
    const trimmed = name.trim();
    if (trimmed === companyName) return;
    if (!trimmed) { setName(companyName); setNameStatus("empty"); return; }
    setNameSaving(true); setNameStatus(null);
    const ok = await updateClient({ name: trimmed });
    setNameSaving(false);
    if (!ok) { setNameStatus("error"); return; }
    setNameStatus("saved");
    if (onCompanyNameChange) onCompanyNameChange(trimmed);
    setTimeout(() => setNameStatus(s => (s === "saved" ? null : s)), 3000);
  };

  const handleSaveContact = async () => {
    if (!selectedClientId || !contactLoaded || contactSaving) return;
    const trimmed = contactName.trim();
    if (trimmed === contactOriginal) return;
    setContactSaving(true); setContactStatus(null);
    const ok = await updateClient({ contact_name: trimmed || null });
    setContactSaving(false);
    if (!ok) { setContactStatus("error"); return; }
    setContactOriginal(trimmed);
    setContactName(trimmed);
    setContactStatus("saved");
    setTimeout(() => setContactStatus(s => (s === "saved" ? null : s)), 3000);
  };

  const handleSaveBilling = async () => {
    if (!selectedClientId) return;
    setBillingSaving(true); setBillingStatus(null);
    const ok = await updateClient({
      billing_tax_id: billing.tax_id.trim() || null,
      billing_address: billing.address.trim() || null,
      billing_city: billing.city.trim() || null,
      billing_postal_code: billing.postal_code.trim() || null,
      billing_country: billing.country.trim() || null,
      billing_payment_method: billing.payment_method.trim() || null,
    });
    setBillingSaving(false);
    if (!ok) { setBillingStatus("error"); return; }
    setBillingStatus("saved");
    setBillingOriginal({ ...billing });
    setTimeout(() => setBillingStatus(s => (s === "saved" ? null : s)), 3000);
  };

  const billingDirty = billingOriginal && Object.keys(billingOriginal).some(k => billing[k] !== billingOriginal[k]);

  // Token de Calendly del cliente activo
  useEffect(() => {
    if (!selectedClientId) { setTokenLoaded(true); return; }
    supabase.from("clients").select("calendly_token").eq("id", selectedClientId).maybeSingle()
      .then(({ data }) => {
        setCalendlyToken(data?.calendly_token || "");
        setSavedToken(data?.calendly_token || "");
        setTokenLoaded(true);
      });
  }, [selectedClientId]);

  const handleSaveToken = async () => {
    if (!selectedClientId) return;
    setTokenSaving(true); setTokenStatus(null);
    const value = calendlyToken.trim() || null;
    const ok = await updateClient({ calendly_token: value });
    setTokenSaving(false);
    if (!ok) { setTokenStatus("error"); return; }
    setSavedToken(value || "");
    setTokenStatus("saved");
    setTimeout(() => setTokenStatus(s => (s === "saved" ? null : s)), 3000);
  };
  const tokenDirty = calendlyToken.trim() !== savedToken;

  // ── Estilos (los de Home, Nueva posición y Evaluación) ─────────────────────
  const card = { background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14 };
  const control = { width: "100%", boxSizing: "border-box", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit", color: INK, background: WHITE, outline: "none", padding: "9px 12px", ...(isMobile ? { fontSize: 16, minHeight: 44 } : null) };
  const onFocus = e => { e.target.style.borderColor = BRAND; };
  const offFocus = e => { e.target.style.borderColor = LINE; };
  const enterBlurs = e => { if (e.key === "Enter") e.target.blur(); };
  const primary = (disabled) => ({ ...(disabled ? btn.disabled : btn.primary), height: isMobile ? 44 : 36, padding: "0 18px", whiteSpace: "nowrap" });
  const ghost = { ...btn.ghost, height: isMobile ? 44 : 36, padding: "0 14px", whiteSpace: "nowrap" };
  const sectionLabel = { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 };
  const savedTxt = <span style={savedText}>{es ? "✓ Guardado" : "✓ Saved"}</span>;
  const errTxt = (msg) => <span style={errorText}>{msg || (es ? "No se pudo guardar. Vuelve a intentarlo." : "Couldn't save. Please try again.")}</span>;
  const loadingPh = es ? "Cargando..." : "Loading...";

  const cardHead = (title, right) => (
    <div style={{ padding: isMobile ? "16px 16px 12px" : "16px 20px 12px", display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, ...(isMobile ? { flexWrap: "wrap" } : null) }}>
      <span style={text.cardTitle}>{title}</span>
      {right}
    </div>
  );

  // Fila "etiqueta | campo", como el detalle de Nueva posición
  const row = (label, field, hint) => (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(150px, 30%) 1fr", gap: isMobile ? 6 : 16, padding: isMobile ? "12px 16px" : "10px 20px", borderTop: `1px solid ${ROW_LINE}`, alignItems: "start" }}>
      <span style={{ fontSize: 12, color: TEXT, paddingTop: isMobile ? 0 : 9 }}>{label}</span>
      <div style={{ minWidth: 0 }}>
        {field}
        {hint && <div style={{ fontSize: 11, color: MUTED, marginTop: 6, lineHeight: 1.5 }}>{hint}</div>}
      </div>
    </div>
  );

  const billingInput = (key, placeholder) => (
    <input type="text" value={billing[key]}
      onChange={e => { const v = e.target.value; setBilling(b => ({ ...b, [key]: v })); setBillingStatus(null); }}
      disabled={!billingLoaded || billingSaving || !selectedClientId}
      placeholder={billingLoaded ? placeholder : loadingPh}
      style={control} onFocus={onFocus} onBlur={offFocus} />
  );

  // Estado de la tarjeta Empresa: un solo aviso en la cabecera
  const companyRight = (nameSaving || contactSaving)
    ? <span style={{ fontSize: 12, color: MUTED }}>{es ? "Guardando..." : "Saving..."}</span>
    : nameStatus === "empty"
      ? errTxt(es ? "El nombre no puede quedar vacío." : "The name can't be empty.")
      : (nameStatus === "error" || contactStatus === "error")
        ? errTxt()
        : (nameStatus === "saved" || contactStatus === "saved")
          ? savedTxt
          : <span style={{ fontSize: 12, color: MUTED }}>{es ? "Se guarda al salir del campo" : "Saves when you leave the field"}</span>;

  const connected = !!savedToken;
  const canDelete = deleteText === "DELETE" && !deleting;

  return (
    <div style={{ padding: isMobile ? 16 : 32 }}>
      <h1 style={pageTitle(isMobile, isMobile ? 16 : 20)}>{t.manageAccount}</h1>

      <div style={{ display: "grid", gridTemplateColumns: isMobile || !isAdmin ? "minmax(0, 1fr)" : "minmax(0, 1fr) 340px", gap: 16, alignItems: "start", ...(!isAdmin && !isMobile ? { maxWidth: 720 } : null) }}>

        {/* ── Izquierda: empresa y facturación ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>

          <div style={{ ...card, overflow: "hidden" }}>
            {cardHead(es ? "Empresa" : "Company", companyRight)}
            {row(es ? "Nombre de la empresa" : "Company name",
              <input type="text" value={name}
                onChange={e => { setName(e.target.value); setNameStatus(null); }}
                onKeyDown={enterBlurs}
                onFocus={onFocus}
                onBlur={e => { offFocus(e); handleSaveName(); }}
                disabled={!selectedClientId}
                placeholder={es ? "Nombre de tu empresa" : "Your company name"}
                style={control} />
            )}
            {row(es ? "Nombre de contacto" : "Contact name",
              <input type="text" value={contactName}
                onChange={e => { setContactName(e.target.value); setContactStatus(null); }}
                onKeyDown={enterBlurs}
                onFocus={onFocus}
                onBlur={e => { offFocus(e); handleSaveContact(); }}
                disabled={!contactLoaded || !selectedClientId}
                placeholder={contactLoaded ? (es ? "Nombre completo" : "Full name") : loadingPh}
                style={control} />
            )}
            {row("Email",
              <div style={{ fontSize: isMobile ? 15 : 13, color: TEXT, paddingTop: isMobile ? 0 : 9, overflowWrap: "anywhere" }}>{userEmail || "—"}</div>
            )}
          </div>

          {isAdmin && <div style={{ ...card, overflow: "hidden" }}>
            {cardHead(es ? "Datos de facturación" : "Billing details",
              <span style={{ fontSize: 12, color: MUTED }}>{es ? "Solo para facturar" : "Used for invoicing only"}</span>)}
            {row(es ? "NIF / CIF / VAT" : "Tax ID (NIF / CIF / VAT)", billingInput("tax_id", "B12345678"))}
            {row(es ? "Dirección" : "Address", billingInput("address", es ? "Calle y número" : "Street and number"))}
            {row(es ? "Ciudad" : "City", billingInput("city", "Madrid"))}
            {row(es ? "Código postal" : "Postal code", billingInput("postal_code", "28004"))}
            {row(es ? "País" : "Country", billingInput("country", es ? "España" : "Spain"))}
            {row(es ? "Forma de pago preferida" : "Preferred payment method",
              billingInput("payment_method", es ? "Transferencia, Bizum..." : "Bank transfer, Bizum, ..."),
              es ? "Solo es una nota para nosotros. Aquí nunca guardamos medios de pago: no escribas números de tarjeta ni datos sensibles." : "Just a note for us. We never store payment methods here: don't enter card numbers or sensitive data."
            )}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, padding: "12px 20px", borderTop: `1px solid ${ROW_LINE}`, ...(isMobile ? { flexDirection: "column-reverse", alignItems: "stretch", gap: 8, padding: "12px 16px 16px", textAlign: "center" } : null) }}>
              {billingStatus === "saved" && savedTxt}
              {billingStatus === "error" && errTxt()}
              <button onClick={handleSaveBilling}
                disabled={billingSaving || !billingLoaded || !selectedClientId || !billingDirty}
                style={primary(billingSaving || !billingLoaded || !selectedClientId || !billingDirty)}>
                {billingSaving ? (es ? "Guardando..." : "Saving...") : (es ? "Guardar" : "Save")}
              </button>
            </div>
          </div>}
        </div>

        {/* ── Derecha: integraciones y baja ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          {isAdmin && <div style={{ ...sectionLabel, margin: "4px 0 -8px" }}>{es ? "Integraciones" : "Integrations"}</div>}

          {isAdmin && <div style={{ ...card, padding: isMobile ? "16px" : "16px 20px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: INK }}>Calendly</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: connected ? TEXT : MUTED }}>
                <span style={{ width: 7, height: 7, borderRadius: 999, background: connected ? GREEN : LINE }} />
                {!tokenLoaded ? loadingPh : connected ? (es ? "Conectado" : "Connected") : (es ? "Sin conectar" : "Not connected")}
              </span>
            </div>
            <p style={{ fontSize: 12, color: MUTED, lineHeight: 1.6, margin: "8px 0 12px" }}>
              {es ? "Muestra las próximas entrevistas en tu Home." : "Shows your upcoming interviews on Home."}
            </p>

            <label style={{ ...sectionLabel, display: "block", marginBottom: 6 }}>Personal Access Token</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input type={showToken ? "text" : "password"} value={calendlyToken}
                onChange={e => { setCalendlyToken(e.target.value); setTokenStatus(null); }}
                placeholder={tokenLoaded ? "eyJraWQ..." : loadingPh}
                disabled={!tokenLoaded || tokenSaving || !selectedClientId}
                style={control} onFocus={onFocus} onBlur={offFocus} />
              <button type="button" onClick={() => setShowToken(v => !v)} style={ghost}>
                {showToken ? (es ? "Ocultar" : "Hide") : (es ? "Mostrar" : "Show")}
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 12, ...(isMobile ? { flexDirection: "column", alignItems: "stretch" } : null) }}>
              <button onClick={() => setShowHelp(v => !v)} style={{ ...linkBtn, ...(isMobile ? { minHeight: 40, textAlign: "left" } : null) }}>
                {showHelp ? (es ? "Ocultar instrucciones" : "Hide instructions") : (es ? "¿Cómo consigo el token?" : "How do I get the token?")}
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: 10, ...(isMobile ? { flexDirection: "column-reverse", alignItems: "stretch", gap: 8, textAlign: "center" } : null) }}>
                {tokenStatus === "saved" && savedTxt}
                {tokenStatus === "error" && errTxt(es ? "No se pudo guardar." : "Couldn't save.")}
                <button onClick={handleSaveToken}
                  disabled={tokenSaving || !tokenLoaded || !selectedClientId || !tokenDirty}
                  style={primary(tokenSaving || !tokenLoaded || !selectedClientId || !tokenDirty)}>
                  {tokenSaving ? (es ? "Guardando..." : "Saving...") : (es ? "Guardar" : "Save")}
                </button>
              </div>
            </div>

            {showHelp && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${ROW_LINE}`, fontSize: 13, color: TEXT, lineHeight: 1.6 }}>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  <li>{es ? "Entra en tu cuenta de Calendly en " : "Log into your Calendly account at "}<a href="https://calendly.com" target="_blank" rel="noreferrer" style={{ color: BRAND }}>calendly.com</a></li>
                  <li>{es ? "Ve a " : "Go to "}<strong>Integrations & apps</strong> → <strong>API & Webhooks</strong></li>
                  <li>{es ? <>Abre el enlace al portal <strong>Calendly Developer</strong></> : <>Open the link to <strong>Calendly Developer</strong> portal</>}</li>
                  <li>{es ? "Pulsa " : "Click "}<strong>Personal Access Tokens</strong> → <strong>Generate new token</strong></li>
                  <li>{es ? "Ponle un nombre (p. ej. \"Rutment\") y copia el token" : "Give it a name (e.g. \"Rutment\") and copy the token"}</li>
                  <li>{es ? "Pégalo arriba y pulsa Guardar" : "Paste it above and click Save"}</li>
                </ol>
                <div style={{ marginTop: 10, fontSize: 12, color: MUTED }}>
                  {es ? "Tu token es privado. Lo guardamos de forma segura y solo lo usamos para cargar tus eventos programados. Puedes revocarlo cuando quieras desde los ajustes de Calendly." : "Your token is private. We store it securely and use it only to load your scheduled events. You can revoke it anytime from your Calendly settings."}
                </div>
              </div>
            )}
          </div>}

          {/* Baja de cuenta. Al final, en gris y con doble paso: es el derecho de
              supresión del RGPD y no puede faltar, pero tampoco debe ser el botón
              más fácil de pulsar. Banner neutro: solo el botón que ejecuta lleva color. */}
          {!showDelete ? (
            <div style={{ padding: "4px 2px" }}>
              <button onClick={() => setShowDelete(true)} style={{ ...linkBtn, fontWeight: 600, color: FAINT, ...(isMobile ? { minHeight: 40 } : null) }}>
                {es ? "Eliminar cuenta" : "Delete account"}
              </button>
            </div>
          ) : (
            // Confirmación en ventana flotante con la palabra DELETE dentro (24/10)
            <ConfirmDialog title={es ? "Eliminar esta cuenta" : "Delete this account"}
              message={es ? "Se eliminan tu acceso, tus posiciones y todas las evaluaciones de candidatos vinculadas. No se puede deshacer." : "This removes your access, your positions and every candidate evaluation linked to them. It cannot be undone."}
              actionLabel={deleting ? (es ? "Enviando..." : "Sending...") : (es ? "Eliminar cuenta" : "Delete account")}
              cancelLabel={es ? "Cancelar" : "Cancel"} busy={!canDelete}
              onConfirm={requestDeletion} onCancel={() => { setShowDelete(false); setDeleteText(""); setDeleteErr(""); }}>
              <p style={{ fontSize: 12, color: MUTED, lineHeight: 1.6, margin: "0 0 14px" }}>
                {es ? "Cerraremos tu sesión ahora y completaremos la eliminación en un máximo de 30 días, como exige el RGPD. Si cambias de opinión antes, escribe a borja@rutment.com." : "We'll log you out now and complete the deletion within 30 days, as required by GDPR. If you change your mind before then, write to borja@rutment.com."}
              </p>
              <label style={{ ...sectionLabel, display: "block", marginBottom: 6 }}>
                {es ? "Escribe DELETE para confirmar" : "Type DELETE to confirm"}
              </label>
              <input value={deleteText} onChange={e => setDeleteText(e.target.value)} placeholder="DELETE"
                style={{ ...control }} onFocus={onFocus} onBlur={offFocus} />
              {deleteErr && <div style={{ ...errorText, marginTop: 10 }}>{deleteErr}</div>}
            </ConfirmDialog>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountView;
